import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import Logo from "./Logo";
import SocialLinks from "./SocialLinks";
import { SERVICES } from "@/data/services";

const Footer = () => {
  return (
    <footer className="bg-ink text-white relative overflow-hidden" data-testid="site-footer">
      {/* Top accent strip */}
      <div className="h-[3px] bg-accent" aria-hidden="true" />

      {/* Big CTA band */}
      <div className="border-b border-white/10">
        <div className="max-w-wide mx-auto px-6 md:px-10 lg:px-14 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6">
                [ Get in touch, today ]
              </div>
              <h2 className="font-display font-bold text-[2.5rem] sm:text-[3.5rem] lg:text-[5rem] leading-[0.98] tracking-tightest text-white">
                Ready for clean air,<br />
                <span className="text-accent">calm spaces?</span>
              </h2>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="footer-cta-contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-accent text-white font-semibold text-[14px] rounded-sm hover:bg-brand transition-colors"
              >
                Start a conversation <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href="mailto:Hello@calmbreeze.co.uk"
                data-testid="footer-cta-email"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/30 text-white font-semibold text-[14px] rounded-sm hover:border-white transition-colors"
              >
                Hello@calmbreeze.co.uk
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Locations + Sitemap */}
      <div className="max-w-wide mx-auto px-6 md:px-10 lg:px-14 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          {/* Logo + tagline */}
          <div className="lg:col-span-4">
            <Logo size="lg" invert withBadge />
            <p className="mt-6 text-[14px] text-white/60 leading-relaxed max-w-xs">
              Clean Air, Calm Spaces. A West London commercial HVAC partner —
              proactive, compliant, quietly excellent.
            </p>
            <div className="mt-8">
              <SocialLinks tone="inverse" size="lg" gap="gap-2.5" testIdPrefix="footer-social" />
            </div>
          </div>

          {/* Location */}
          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-5">
              [ West London ]
            </div>
            <div className="space-y-4 text-[14px] text-white/70">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                <span>
                  14 Hyde Lodge,<br />
                  11 Pennyroyal Drive,<br />
                  West Drayton, UB7 9FF
                </span>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                <a href="mailto:Hello@calmbreeze.co.uk" className="hover:text-white transition-colors" data-testid="footer-email">
                  Hello@calmbreeze.co.uk
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                <a href="tel:+441895919333" className="hover:text-white transition-colors" data-testid="footer-phone">
                  01895 919 333
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                <a href="https://wa.me/447881521243" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="footer-whatsapp">
                  +44 7881 521 243 · WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-5">
              [ Services ]
            </div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-[14px] text-white/70 hover:text-white transition-colors"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Calmbreeze */}
          <div className="lg:col-span-2">
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-5">
              [ Company ]
            </div>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-[14px] text-white/70 hover:text-white transition-colors">About</Link></li>
              <li><Link to="/industries" className="text-[14px] text-white/70 hover:text-white transition-colors">Industries</Link></li>
              <li><Link to="/case-studies" className="text-[14px] text-white/70 hover:text-white transition-colors">Case Studies</Link></li>
              <li><Link to="/sustainability" className="text-[14px] text-white/70 hover:text-white transition-colors">Sustainability</Link></li>
              <li><Link to="/insights" className="text-[14px] text-white/70 hover:text-white transition-colors">Insights</Link></li>
              <li><Link to="/resources" className="text-[14px] text-white/70 hover:text-white transition-colors">Free resources</Link></li>
              <li><Link to="/contact" className="text-[14px] text-white/70 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom legal bar */}
        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="text-[12px] text-white/40 font-mono tracking-wider leading-relaxed">
            © {new Date().getFullYear()} Calmbreeze · All rights reserved
            <br />
            <span className="text-white/30 normal-case">
              Calmbreeze is a trading name of Coles HVAC Services Ltd · Company No. 11814249 · Registered in England &amp; Wales
            </span>
          </div>
          <div className="flex items-center gap-5 text-[12px] text-white/50 font-mono tracking-wider flex-wrap">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">Cookies</Link>
            <span>·</span>
            <span>West London</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
